import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  type Ref,
} from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemErro } from '../utils';
import CampoArea from '@/components/base/CampoArea';
import CampoTexto from '@/components/base/CampoTexto';
import BlocoRecebedor from './BlocoRecebedor';
import PagamentoLegado from './PagamentoLegado';
import type { EventoDraft, ModoPagamento } from '../types';

export interface PassoPagamentoHandle {
  validar: () => boolean;
}

interface PassoPagamentoProps {
  eventoId: number | null;
  draft: EventoDraft;
  onChange: (patch: Partial<EventoDraft>) => void;
}

// Contrato da função `como_recebe_o_evento` (leitura). Nada aqui é recalculado
// no navegador — é tudo resposta do banco.
export type FormaPagamento = 'gratuito' | 'pix_app' | 'sorteio' | 'fora_do_app';
export type DestinoDinheiro = 'nao_se_aplica' | 'plataforma' | 'organizador';
export type Pendencia = 'chave_pix' | 'conectar_mp' | 'reconectar_mp';

interface ContaMP {
  numero: string;
  vale_ate: string;
  vencendo: boolean;
}

interface ChavePixInfo {
  nome: string;
  mascarada: string;
}

interface SorteioInfo {
  id: number;
  status: string;
  numeros: number;
  por_talao: number;
  taloes: number;
  vendidos: number;
}

interface ComissaoInfo {
  plataforma_pct: number;
  plataforma_reais: number;
  gateway_reais: number;
  organizador_reais: number;
  numero_reais: number;
}

interface ComoRecebe {
  forma: FormaPagamento;
  destino: DestinoDinheiro;
  precisa: Pendencia | null;
  frase_forma: string;
  frase_destino: string;
  pendencia: string | null;
  conta_mp: ContaMP | null;
  chave_pix: ChavePixInfo | null;
  sorteio: SorteioInfo | null;
  comissao_texto: string | null;
  comissao: ComissaoInfo | null;
  // Campos de "Direto com a organização" (quando o banco devolve).
  whatsapp?: string | null;
  texto_pagamento?: string | null;
  como_participar?: string | null;
  /** Alguns retornos trazem o objeto `p` cru. */
  p?: { whatsapp?: string | null; como_participar?: string | null } | null;
}

// Retorno da função `definir_destino_do_dinheiro` (escrita).
interface ResultadoDestino {
  ok: boolean;
  trancado?: boolean;
  erro?: string;
  precisa?: Pendencia | null;
  link_para_conectar?: string | null;
  vale_ate?: string | null;
}

const FORMAS: { valor: FormaPagamento; icone: string; titulo: string; descricao: string }[] = [
  {
    valor: 'gratuito',
    icone: 'ri-gift-line',
    titulo: 'Não se paga',
    descricao: 'Evento gratuito. A pessoa só preenche a ficha.',
  },
  {
    valor: 'pix_app',
    icone: 'ri-bank-card-line',
    titulo: 'Pix pelo app',
    descricao: 'Paga na hora da inscrição, dentro da plataforma.',
  },
  {
    valor: 'sorteio',
    icone: 'ri-ticket-2-line',
    titulo: 'Vendendo números de uma ação entre amigos',
    descricao:
      'Cada inscrito ganha um talão. O que ele vende abate a inscrição dele. Quem não vender tudo compra os números que faltam — e concorre aos prêmios do mesmo jeito.',
  },
  {
    valor: 'fora_do_app',
    icone: 'ri-hand-heart-line',
    titulo: 'Direto com a organização',
    descricao: 'A plataforma divulga e recolhe as fichas. O pagamento é combinado fora.',
  },
];

const DESTINOS: { valor: DestinoDinheiro; icone: string; titulo: string; descricao: string }[] = [
  {
    valor: 'plataforma',
    icone: 'ri-shield-check-line',
    titulo: 'Cai na conta da plataforma e nós repassamos',
    descricao: 'Você indica a chave Pix que recebe os repasses.',
  },
  {
    valor: 'organizador',
    icone: 'ri-store-2-line',
    titulo: 'Cai direto na conta do organizador',
    descricao: 'Conecte o Mercado Pago uma única vez — vale por 6 meses.',
  },
];

function formaParaModo(forma: FormaPagamento): ModoPagamento {
  switch (forma) {
    case 'pix_app':
      return 'plataforma';
    case 'sorteio':
      return 'rifa';
    case 'fora_do_app':
      return 'direto';
    default:
      return 'gratuito';
  }
}

function formatarNumero(n: number): string {
  return n.toLocaleString('pt-BR');
}

function PassoPagamento(
  { eventoId, draft, onChange }: PassoPagamentoProps,
  ref: Ref<PassoPagamentoHandle>,
) {
  const [carregando, setCarregando] = useState(true);
  const [funcaoIndisponivel, setFuncaoIndisponivel] = useState(false);
  const [dados, setDados] = useState<ComoRecebe | null>(null);
  const [editandoForma, setEditandoForma] = useState(false);
  const [editandoDestino, setEditandoDestino] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);

  // "Direto com a organização": dois campos, gravados ao sair do campo.
  const [foraApp, setForaApp] = useState({ whatsapp: '', comoParticipar: '' });
  // Os campos extras aparecem assim que a pessoa escolhe "Direto com a organização"
  // — mesmo se o banco ainda estiver respondendo.
  const [extrasAbertos, setExtrasAbertos] = useState(false);

  // Confirmação ao apagar uma ação entre amigos já montada.
  const [confirmandoApagar, setConfirmandoApagar] = useState<FormaPagamento | null>(null);

  // Link de conexão do Mercado Pago, devolvido por `definir_destino_do_dinheiro`.
  const [conexaoMP, setConexaoMP] = useState<{ link: string; vale_ate: string } | null>(null);
  const [linkCopiado, setLinkCopiado] = useState(false);

  const recarregar = useCallback(async (id: number): Promise<ComoRecebe> => {
    const { data, error } = await supabase.rpc('como_recebe_o_evento', { p_evento: id });
    if (error) throw error;
    return data as ComoRecebe;
  }, []);

  // Mantém os campos extras iguais ao que o banco devolveu (quando ele devolve).
  const sincronizarExtras = useCallback((d: ComoRecebe) => {
    const doP = d.p ?? null;
    const whats = typeof d.whatsapp === 'string' ? d.whatsapp : doP?.whatsapp;
    const texto =
      typeof d.como_participar === 'string'
        ? d.como_participar
        : (doP?.como_participar ?? d.texto_pagamento);
    if (typeof whats === 'string' || typeof texto === 'string') {
      setForaApp((f) => ({
        whatsapp: typeof whats === 'string' ? whats : f.whatsapp,
        comoParticipar: typeof texto === 'string' ? texto : f.comoParticipar,
      }));
    }
  }, []);

  useEffect(() => {
    if (!eventoId) {
      setCarregando(false);
      setFuncaoIndisponivel(true);
      return;
    }
    let ativo = true;
    setCarregando(true);
    setFuncaoIndisponivel(false);

    recarregar(eventoId)
      .then((d) => {
        if (!ativo) return;
        setDados(d);
        sincronizarExtras(d);
        if (d.forma === 'fora_do_app') setExtrasAbertos(true);
        onChange({ modoPagamento: formaParaModo(d.forma) });
      })
      .catch(() => {
        if (ativo) setFuncaoIndisponivel(true);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  useImperativeHandle(ref, () => ({
    validar() {
      // "Direto com a organização" sem WhatsApp não avança (não dá para a pessoa pagar).
      if ((dados?.forma === 'fora_do_app' || extrasAbertos) && !foraApp.whatsapp.trim()) {
        setErroSalvar('Falta o WhatsApp para quem quiser pagar.');
        document
          .getElementById('whatsapp-pagamento')
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }
      setErroSalvar(null);
      return true;
    },
  }));

  async function salvarForma(forma: FormaPagamento, p: Record<string, unknown>) {
    if (!eventoId) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const { error } = await supabase.rpc('definir_forma_de_pagamento', {
        p_evento: eventoId,
        p_forma: forma,
        p,
      });
      if (error) throw error;
      const d = await recarregar(eventoId);
      setDados(d);
      sincronizarExtras(d);
      onChange({ modoPagamento: formaParaModo(forma) });
      setEditandoForma(false);
      setConfirmandoApagar(null);
      if (forma === 'pix_app' || forma === 'sorteio') setEditandoDestino(true);
    } catch (e) {
      setErroSalvar(mensagemErro(e, 'Não foi possível salvar. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  }

  function escolherForma(forma: FormaPagamento) {
    setErroSalvar(null);

    // Trocar de "ação entre amigos" para outra forma pede confirmação.
    if (dados?.forma === 'sorteio' && forma !== 'sorteio') {
      setConfirmandoApagar(forma);
      return;
    }

    // "Direto com a organização" vale na hora: grava a escolha e mostra os
    // campos extras (que salvam ao sair do campo).
    if (forma === 'fora_do_app') {
      setExtrasAbertos(true);
      setEditandoForma(false);
      void salvarForma('fora_do_app', {
        whatsapp: foraApp.whatsapp.trim() || null,
        como_participar: foraApp.comoParticipar.trim() || null,
      });
      return;
    }

    setExtrasAbertos(false);
    void salvarForma(forma, {});
  }

  async function salvarForaApp() {
    const p = {
      whatsapp: foraApp.whatsapp.trim() || null,
      como_participar: foraApp.comoParticipar.trim() || null,
    };
    await salvarForma('fora_do_app', p);
  }

  async function confirmarApagarSorteio() {
    if (!confirmandoApagar) return;
    await salvarForma(confirmandoApagar, { confirmo_apagar_sorteio: true });
  }

  async function escolherDestino(destino: DestinoDinheiro) {
    if (!eventoId) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const { data, error } = await supabase.rpc('definir_destino_do_dinheiro', {
        p_evento: eventoId,
        p_destino: destino,
      });
      if (error) throw error;

      const r = (data as ResultadoDestino | null) || {};

      // Evento trancado: mostra o motivo e não insiste.
      if (r.trancado || r.ok === false) {
        setErroSalvar(
          mensagemErro(r.erro, 'Este evento não permite mudar para onde vai o dinheiro.'),
        );
        const d = await recarregar(eventoId);
        setDados(d);
        setEditandoDestino(false);
        return;
      }

      // Precisa conectar/reconectar o Mercado Pago: guarda o link pronto.
      if (r.precisa === 'conectar_mp' || r.precisa === 'reconectar_mp') {
        if (r.link_para_conectar) {
          setConexaoMP({ link: r.link_para_conectar, vale_ate: r.vale_ate || '' });
        }
      } else {
        setConexaoMP(null);
      }

      const d = await recarregar(eventoId);
      setDados(d);
      setEditandoDestino(false);
    } catch (e) {
      setErroSalvar(mensagemErro(e, 'Não foi possível salvar. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  }

  // Gera um convite novo para conectar o Mercado Pago. Só chama quando a pessoa
  // clica — nunca ao redesenhar a tela (cada chamada cria um convite novo).
  async function gerarLinkMP() {
    if (!eventoId) return;
    setSalvando(true);
    setErroSalvar(null);
    try {
      const { data, error } = await supabase.rpc('definir_destino_do_dinheiro', {
        p_evento: eventoId,
        p_destino: 'organizador',
      });
      if (error) throw error;

      const r = (data as ResultadoDestino | null) || {};
      if (r.trancado || r.ok === false) {
        setErroSalvar(mensagemErro(r.erro, 'Não foi possível gerar o link de conexão.'));
        return;
      }
      if (r.link_para_conectar) {
        setConexaoMP({ link: r.link_para_conectar, vale_ate: r.vale_ate || '' });
      }
    } catch (e) {
      setErroSalvar(mensagemErro(e, 'Não foi possível gerar o link. Tente novamente.'));
    } finally {
      setSalvando(false);
    }
  }

  function copiarLink(link: string) {
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopiado(true);
      window.setTimeout(() => setLinkCopiado(false), 2000);
    });
  }

  function linkWhatsApp(link: string): string {
    return `https://wa.me/?text=${encodeURIComponent(link)}`;
  }

  // Estados de carregamento / migração.
  if (carregando) {
    return (
      <div className="space-y-8">
        <div>
          <div className="h-7 w-56 animate-pulse rounded bg-secondary-100" />
          <div className="mt-2 h-4 w-72 animate-pulse rounded bg-secondary-100" />
        </div>
        <div className="h-40 w-full animate-pulse rounded-lg bg-secondary-100" />
        <div className="h-40 w-full animate-pulse rounded-lg bg-secondary-100" />
      </div>
    );
  }

  // Durante a migração, se a função ainda não existir, mostra a tela antiga.
  if (funcaoIndisponivel || !dados) {
    return <PagamentoLegado eventoId={eventoId} draft={draft} onChange={onChange} />;
  }

  const mexeComDinheiro = dados.forma === 'pix_app' || dados.forma === 'sorteio';
  const precisaConectarMP = dados.precisa === 'conectar_mp' || dados.precisa === 'reconectar_mp';

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">Como se paga a inscrição</h2>
        <p className="mt-1 text-sm text-secondary-500">
          Duas coisas separadas: como a pessoa paga e, se houver dinheiro, para onde ele vai.
        </p>
      </div>

      {erroSalvar && (
        <p className="rounded-md bg-danger-50 px-4 py-3 text-sm text-danger-700" data-erro="true">
          {erroSalvar}
        </p>
      )}

      {/* PERGUNTA 1 — Como as pessoas pagam */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Como as pessoas pagam</h3>

        {editandoForma ? (
          <div className="mt-3">
            {confirmandoApagar ? (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                <p className="text-sm text-foreground-800">
                  Este evento já tem uma ação entre amigos montada. Trocar a forma de pagamento
                  apaga a ação entre amigos e os números já vendidos. Tem certeza de que quer continuar?
                </p>
                <div className="mt-3 flex gap-2">
                  <button
                    type="button"
                    disabled={salvando}
                    onClick={confirmarApagarSorteio}
                    className="cursor-pointer whitespace-nowrap rounded-md bg-danger-600 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-danger-700 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {salvando ? 'Apagando…' : 'Apagar e trocar'}
                  </button>
                  <button
                    type="button"
                    disabled={salvando}
                    onClick={() => setConfirmandoApagar(null)}
                    className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  {FORMAS.map((o) => {
                    const ativo = dados.forma === o.valor;
                    return (
                      <button
                        key={o.valor}
                        type="button"
                        disabled={salvando}
                        onClick={() => escolherForma(o.valor)}
                        className={`flex w-full cursor-pointer items-start gap-3 rounded-lg border p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          ativo
                            ? 'border-primary-500 bg-primary-50'
                            : 'border-secondary-200 bg-white hover:border-primary-300'
                        }`}
                      >
                        <span
                          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            ativo ? 'border-primary-500' : 'border-secondary-300'
                          }`}
                        >
                          {ativo && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 text-[15px] font-medium text-foreground-950">
                            <span className="flex h-5 w-5 items-center justify-center text-secondary-500">
                              <i className={o.icone} />
                            </span>
                            {o.titulo}
                          </span>
                          <span className="mt-0.5 block text-sm text-secondary-600">
                            {o.descricao}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  disabled={salvando}
                  onClick={() => setEditandoForma(false)}
                  className="mt-3 cursor-pointer whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancelar
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="mt-3 flex items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="flex items-start gap-2 text-[15px] text-foreground-900">
                <span className="mt-1 flex h-2 w-2 shrink-0 rounded-full bg-foreground-950" />
                <span>{dados.frase_forma}</span>
              </p>
              {dados.sorteio && (
                <p className="pl-4 text-sm text-secondary-600">
                  {formatarNumero(dados.sorteio.numeros)} números ·{' '}
                  {formatarNumero(dados.sorteio.por_talao)} por talão ·{' '}
                  {formatarNumero(dados.sorteio.taloes)} talões
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setErroSalvar(null);
                setEditandoForma(true);
              }}
              className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
            >
              trocar
            </button>
          </div>
        )}

        {/* Extras do "Direto com a organização": salvam ao sair do campo. */}
        {!editandoForma && (dados.forma === 'fora_do_app' || extrasAbertos) && (
          <div
            className="mt-4 space-y-4 rounded-lg border border-secondary-200 bg-background-50 p-4"
            onBlur={() => void salvarForaApp()}
          >
            <CampoTexto
              id="whatsapp-pagamento"
              name="whatsapp"
              label="WhatsApp para quem quiser pagar"
              help="Aparece para quem se inscreveu, para acertar o pagamento."
              value={foraApp.whatsapp}
              onChange={(v) => setForaApp((f) => ({ ...f, whatsapp: v }))}
              inputMode="tel"
              placeholder="14997889732"
              required
            />
            <CampoArea
              id="como-participar"
              name="como_participar"
              label="O que dizer para quem quiser pagar"
              help="Opcional. Explica onde e como acertar o pagamento com a organização."
              value={foraApp.comoParticipar}
              onChange={(v) => setForaApp((f) => ({ ...f, comoParticipar: v }))}
              placeholder="Procure a secretaria da paróquia de terça a sexta, das 14h às 17h."
            />
          </div>
        )}
      </section>

      {/* PERGUNTA 2 — Para onde vai o dinheiro */}
      <section className="rounded-lg border border-secondary-200 bg-white p-5">
        <h3 className="text-base font-medium text-foreground-950">Para onde vai o dinheiro</h3>

        {!mexeComDinheiro ? (
          <p className="mt-2 text-sm text-secondary-600">
            A plataforma não recebe nada deste evento.
          </p>
        ) : editandoDestino ? (
          <div className="mt-3 space-y-2">
            {DESTINOS.map((o) => {
              const ativo = dados.destino === o.valor;
              return (
                <button
                  key={o.valor}
                  type="button"
                  disabled={salvando}
                  onClick={() => escolherDestino(o.valor)}
                  className={`flex w-full cursor-pointer items-start gap-3 rounded-lg border p-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                    ativo
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-secondary-200 bg-white hover:border-primary-300'
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                      ativo ? 'border-primary-500' : 'border-secondary-300'
                    }`}
                  >
                    {ativo && <span className="h-2 w-2 rounded-full bg-primary-500" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2 text-[15px] font-medium text-foreground-950">
                      <span className="flex h-5 w-5 items-center justify-center text-secondary-500">
                        <i className={o.icone} />
                      </span>
                      {o.titulo}
                    </span>
                    <span className="mt-0.5 block text-sm text-secondary-600">{o.descricao}</span>
                  </span>
                </button>
              );
            })}
            <button
              type="button"
              disabled={salvando}
              onClick={() => setEditandoDestino(false)}
              className="cursor-pointer whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-secondary-600 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        ) : (
          <div className="mt-3">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <p className="flex items-start gap-2 text-[15px] text-foreground-900">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-green-600">
                    <i className="ri-check-line" />
                  </span>
                  <span>{dados.frase_destino}</span>
                </p>

                {dados.destino === 'plataforma' && dados.chave_pix && (
                  <p className="pl-7 text-sm text-secondary-600">
                    {dados.chave_pix.nome} ({dados.chave_pix.mascarada})
                  </p>
                )}

                {dados.destino === 'organizador' && dados.conta_mp && (
                  <p className="pl-7 text-sm text-secondary-600">
                    Conta {dados.conta_mp.numero} · vale até {dados.conta_mp.vale_ate}
                  </p>
                )}

                {dados.comissao_texto && (
                  <p className="pl-7 text-sm text-secondary-600">{dados.comissao_texto}</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setErroSalvar(null);
                  setEditandoDestino(true);
                }}
                className="cursor-pointer whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
              >
                trocar
              </button>
            </div>

            {/* Aviso (não bloqueio) quando a autorização do MP está vencendo. */}
            {dados.conta_mp?.vencendo && (
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-amber-700">
                  <i className="ri-information-line text-lg" />
                </span>
                <p className="text-sm text-amber-800">
                  A autorização vence em {dados.conta_mp.vale_ate}. Vale reconectar antes.
                </p>
              </div>
            )}

            {/* Destino definido, mas falta a chave Pix (repasse pela plataforma). */}
            {dados.precisa === 'chave_pix' && (
              <div className="mt-4">
                <BlocoRecebedor eventoId={eventoId} />
              </div>
            )}

            {/* Destino definido, mas falta conectar o Mercado Pago. */}
            {precisaConectarMP && (
              <div className="mt-4 rounded-lg border border-secondary-200 bg-background-50 p-4">
                {dados.pendencia && (
                  <p className="text-sm text-foreground-800">{dados.pendencia}</p>
                )}

                {conexaoMP ? (
                  <div className="mt-3 space-y-3">
                    <p className="text-sm text-foreground-800">
                      Envie este link para quem vai receber (o tesoureiro). Ele abre no celular,
                      já logado na conta que vai receber o dinheiro.
                    </p>
                    <div className="flex items-center gap-2 rounded-md border border-secondary-200 bg-white p-2">
                      <p className="min-w-0 flex-1 truncate text-sm text-secondary-700">
                        {conexaoMP.link}
                      </p>
                      <button
                        type="button"
                        onClick={() => copiarLink(conexaoMP.link)}
                        className="cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 px-3 py-1.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                      >
                        {linkCopiado ? 'Copiado!' : 'Copiar'}
                      </button>
                      <a
                        href={linkWhatsApp(conexaoMP.link)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="cursor-pointer whitespace-nowrap rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-green-700"
                      >
                        Enviar por WhatsApp
                      </a>
                    </div>
                    {conexaoMP.vale_ate && (
                      <p className="text-xs text-secondary-500">
                        O link vale até {conexaoMP.vale_ate}.
                      </p>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={salvando}
                    onClick={gerarLinkMP}
                    className="mt-3 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {salvando
                      ? 'Gerando…'
                      : dados.precisa === 'reconectar_mp'
                        ? 'Gerar link para reconectar o Mercado Pago'
                        : 'Gerar link para conectar o Mercado Pago'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

export default forwardRef(PassoPagamento);